import { FC, useCallback, useContext } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { Button, Chip, Stack, Typography } from '@mui/material';
import { Link, MenuBook } from '@mui/icons-material';
import { isEmpty } from 'lodash';

import { DialogContext } from '@cvt/contexts';
import { MessageFeedbackView } from '@cvt/components/MessageFeedbackView';
import { useDictionary } from '@cvt/hooks/useDictionary';

import { BoxWell } from '@shared/components/BoxWell';

import { useMeetingQuery } from '@modules/Meetings/hooks/useMeetingQuery';
import { useMeetingCrud } from '@modules/Meetings/hooks/useMeetingCrud';

import { StoryThroughFieldArrayForm } from './StoryThroughFieldArrayForm';


export type FormCrud = Meetings.Create | Meetings.Edit;

interface Props {
    meetingId: number;
};

export const StoriesThroughSection: FC<Props> = (props) => {
  const { meetingId } = props;
  const { openDialog, asyncConfirmation } = useContext(DialogContext);
  const dictionary = useDictionary();
  const methods = useFormContext<FormCrud>();

  const { control } = methods;

  const { fields } = useFieldArray({
    control,
    keyName: 'fieldId',
    name: 'storiesThrough',
  });

  const { meeting } = useMeetingQuery({ id: meetingId }, { enabled: !!meetingId });
  const { editMeeting } = useMeetingCrud();

  const onLinkStory = useCallback(() => {
    openDialog('meetingLinkStories', {
      meetingId,
    });
  }, [meetingId, openDialog]);

  const onUnlinkStory = useCallback(async (storyId: number) => {
    const confirmed = await asyncConfirmation({ title: dictionary.meetings.edit.unlinkStoryConfirmation });
    if (!confirmed) {
      return false;
    }
    try {
      const storiesThroughs = meeting?.storiesThrough?.filter(storyThrough => storyThrough.story.id !== storyId) || [];
      const payload = {
        id: meetingId,
        storiesThrough: storiesThroughs.map(storyThrough => ({
          story: { id: storyThrough.story.id },
        })),
      };
      await editMeeting.mutateAsync(payload as Meetings.Edit);
    } catch (error) {
      console.error(error);
    }
  }, [asyncConfirmation, dictionary, editMeeting, meeting, meetingId]);

  return (
    <BoxWell sx={{ p: { xs: 2, sm: 5 } }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} justifyContent="space-between" alignItems="center">
        <Stack direction="row" gap={1} alignItems="center">
          <MenuBook color="primary" sx={{ fontSize: 16 }}/>
          <Typography variant="body1" fontWeight={600}>
            Linked Stories
          </Typography>
          <Chip
            label={meeting?.storiesThrough?.length || 0}
            size="small"
            color="secondary"
            sx={{ height: 'auto', fontSize: 12, fontWeight: 500 }}
          />
        </Stack>
        <Button
          onClick={onLinkStory}
          color="primary"
          size="small"
          startIcon={<Link/>}
          variant="outlined"
        >
          Link Story
        </Button>
      </Stack>

      {isEmpty(meeting?.storiesThrough) && (
        <MessageFeedbackView
          message="There are no stories linked to this meeting."
          height={100}
        />
      )}

      <Stack gap={1} mt={2}>
        {fields?.map((field, index) => (
          <StoryThroughFieldArrayForm
            key={field.fieldId}
            fieldPath={`storiesThrough[${index}]`}
            storyThrough={field}
            onDelete={onUnlinkStory}
          />
        ))}
      </Stack>

    </BoxWell>
  );
};
